/**
 * AUTH PROVIDERS - DO NOT MODIFY WITHOUT READING
 *
 * The runtime config selects exactly one provider to wrap the app:
 * - Okta: lens.jamfconcepts.io (production/jamf-internal)
 * - Basic auth: username/password sessions (lensdemo, pilot tenants)
 * - Passcode: shared passcode (legacy lensdemo)
 *
 * With none configured, a production build renders an error instead of the app.
 * Only the Vite dev server falls back to the unauthenticated dev mode.
 *
 * See frontend/CLAUDE.md "Authentication" section for details.
 */
import React, { useState } from 'react'
import ReactDOM from 'react-dom/client'
import { BrowserRouter, useLocation } from 'react-router-dom'
import { Security, LoginCallback } from '@okta/okta-react'
import { OktaAuth, toRelativeUrl } from '@okta/okta-auth-js'
import { ApiProvider } from './contexts/ApiContext'
import { PasscodeScreen, getPasscodeToken } from './components/PasscodeScreen'
import { BasicLoginScreen, getBasicAuthToken, clearBasicAuthToken } from './components/BasicLoginScreen'
import { LoggedOut } from './components/LoggedOut'
import { clearLoggedOut, isLoggedOut } from './hooks/logoutState'
import App from './App'
import './index.css'

// Runtime config type (injected by docker-entrypoint.sh in production)
declare global {
  interface Window {
    __RUNTIME_CONFIG__?: {
      // Okta configuration (jamf-internal)
      OKTA_ISSUER?: string
      OKTA_CLIENT_ID?: string
      OKTA_REDIRECT_URI?: string
      OKTA_ENABLED?: boolean | string
      // Passcode configuration (lensdemo only)
      PASSCODE_ENABLED?: boolean | string
      // Basic auth configuration (pilot tenants)
      BASIC_AUTH_ENABLED?: boolean | string
      // API key for local dev without auth
      LENS_API_KEY?: string
    }
  }
}

// Get config from runtime (production) or Vite env vars (development)
const runtimeConfig = window.__RUNTIME_CONFIG__ || {}

// Okta configuration
const oktaIssuer = runtimeConfig.OKTA_ISSUER || import.meta.env.VITE_OKTA_ISSUER || ''
const oktaClientId = runtimeConfig.OKTA_CLIENT_ID || import.meta.env.VITE_OKTA_CLIENT_ID || ''
const oktaRedirectUri = runtimeConfig.OKTA_REDIRECT_URI || import.meta.env.VITE_OKTA_REDIRECT_URI || `${window.location.origin}/callback`
// An explicit false flag from runtime config is authoritative — a configured
// issuer/client-id must not force a provider back on once an operator disabled it.
const oktaExplicitlyOff = runtimeConfig.OKTA_ENABLED === false || runtimeConfig.OKTA_ENABLED === 'false'
const oktaEnabled = !oktaExplicitlyOff && (runtimeConfig.OKTA_ENABLED === true || runtimeConfig.OKTA_ENABLED === 'true' || (!!oktaIssuer && !!oktaClientId))

// Passcode configuration (lensdemo only) — only takes effect when Okta is disabled
const passcodeEnabled = (runtimeConfig.PASSCODE_ENABLED === true || runtimeConfig.PASSCODE_ENABLED === 'true') && !oktaEnabled

// Basic auth configuration (pilot tenants) — mutually exclusive with all other providers
const basicAuthEnabled = (runtimeConfig.BASIC_AUTH_ENABLED === true || runtimeConfig.BASIC_AUTH_ENABLED === 'true') && !oktaEnabled && !passcodeEnabled

// Log auth configuration
console.log('[Auth] Config:', {
  oktaEnabled,
  passcodeEnabled,
  basicAuthEnabled,
  oktaIssuer: oktaIssuer ? '***configured***' : '',
})

// Initialize OktaAuth client (only if Okta is enabled)
// Note: 'groups' scope is required to receive group membership in ID token
// The Okta app must also have Groups claim configured under Sign On → OpenID Connect ID Token
const oktaAuth = oktaEnabled && oktaIssuer && oktaClientId
  ? new OktaAuth({
      issuer: oktaIssuer,
      clientId: oktaClientId,
      redirectUri: oktaRedirectUri,
      scopes: ['openid', 'profile', 'email', 'groups'],
      pkce: true,
    })
  : null

// Restore original URI after Okta login redirect
const restoreOriginalUri = async (_oktaAuth: OktaAuth, originalUri: string) => {
  console.log('[Okta] restoreOriginalUri called with:', originalUri)
  const targetUrl = toRelativeUrl(originalUri || '/', window.location.origin)
  console.log('[Okta] Redirecting to:', targetUrl)
  window.location.replace(targetUrl)
}

// Wrapper that handles callback route BEFORE ApiProvider blocks rendering
function OktaAppWrapper() {
  const location = useLocation()

  // Handle callback route directly - don't let ApiProvider block it
  if (location.pathname === '/callback') {
    console.log('[Okta] Rendering LoginCallback directly (bypassing ApiProvider)')
    return (
      <LoginCallback
        errorComponent={({ error }: { error: Error }) => {
          console.error('[Okta] LoginCallback error:', error)
          return (
            <div style={{ padding: '2rem', fontFamily: 'monospace' }}>
              <h2>Authentication Error</h2>
              <p><strong>Name:</strong> {error.name}</p>
              <p><strong>Message:</strong> {error.message}</p>
              <pre style={{ background: '#f4f4f4', padding: '1rem', overflow: 'auto' }}>
                {JSON.stringify(error, Object.getOwnPropertyNames(error), 2)}
              </pre>
              <button onClick={() => window.location.href = '/'}>Return to Home</button>
            </div>
          )
        }}
      />
    )
  }

  // All other routes go through ApiProvider
  return (
    <ApiProvider>
      <App />
    </ApiProvider>
  )
}

// Basic auth gate (pilot tenants) - blocks the app behind BasicLoginScreen until
// a session token has been issued. The token is sent as a Bearer header by api.ts.
function BasicAuthGate({ children }: { children: React.ReactNode }) {
  const [authed, setAuthed] = useState(() => !!getBasicAuthToken())
  const [loggedOut, setLoggedOutState] = useState(() => isLoggedOut())

  if (loggedOut && !authed) {
    return (
      <LoggedOut
        onSignIn={() => {
          clearLoggedOut()
          clearBasicAuthToken()
          setLoggedOutState(false)
          setAuthed(false)
        }}
      />
    )
  }

  if (!authed) return <BasicLoginScreen onSuccess={() => setAuthed(true)} />
  return <>{children}</>
}

// Passcode gate (lensdemo only) - blocks the app behind PasscodeScreen until a
// session token has been issued. The token is sent as a Bearer header by api.ts.
function PasscodeGate({ children }: { children: React.ReactNode }) {
  const [authed, setAuthed] = useState(() => !!getPasscodeToken())
  const [loggedOut, setLoggedOutState] = useState(() => isLoggedOut())

  // Just logged out: show the landing page instead of the passcode prompt so a
  // fresh token isn't immediately requested. "Sign in again" clears the flag
  // and returns to PasscodeScreen.
  if (loggedOut && !authed) {
    return (
      <LoggedOut
        onSignIn={() => {
          clearLoggedOut()
          setLoggedOutState(false)
        }}
      />
    )
  }

  if (!authed) return <PasscodeScreen onSuccess={() => setAuthed(true)} />
  return <>{children}</>
}

// Determine which auth provider to use
let AppWithAuth: React.ReactNode

if (basicAuthEnabled) {
  console.log('[Auth] Using basic authentication')
  AppWithAuth = (
    <BrowserRouter>
      <BasicAuthGate>
        <ApiProvider>
          <App />
        </ApiProvider>
      </BasicAuthGate>
    </BrowserRouter>
  )
} else if (passcodeEnabled) {
  console.log('[Auth] Using passcode authentication')
  AppWithAuth = (
    <BrowserRouter>
      <PasscodeGate>
        <ApiProvider>
          <App />
        </ApiProvider>
      </PasscodeGate>
    </BrowserRouter>
  )
} else if (oktaEnabled && oktaAuth) {
  console.log('[Auth] Using Okta authentication')
  AppWithAuth = (
    <BrowserRouter>
      <Security oktaAuth={oktaAuth} restoreOriginalUri={restoreOriginalUri}>
        <OktaAppWrapper />
      </Security>
    </BrowserRouter>
  )
} else if (import.meta.env.DEV) {
  console.log('[Auth] No authentication configured - running in dev mode')
  AppWithAuth = (
    <BrowserRouter>
      <ApiProvider>
        <App />
      </ApiProvider>
    </BrowserRouter>
  )
} else {
  // Fail closed: a deployed build with no provider must not render the app.
  console.error('[Auth] No authentication provider configured')
  AppWithAuth = (
    <div style={{ padding: '2rem', fontFamily: 'system-ui, sans-serif' }}>
      <h2>Sign-in is not configured</h2>
      <p>This deployment has no authentication provider enabled. Contact your administrator.</p>
    </div>
  )
}

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    {AppWithAuth}
  </React.StrictMode>,
)
